import { useEffect, useRef, useState, useCallback } from "react";
import { Video, VideoOff, ExternalLink, RefreshCw } from "lucide-react";
import api from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function CameraTile({ cam, refreshMs = 12000 }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const [t, setT] = useState(() => Date.now() + Math.random() * 1000);
  const [errored, setErrored] = useState(false);
  const offline = (cam.state || "").toUpperCase() !== "CONNECTED";

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!inView || offline) return;
    const jitter = Math.random() * 4000;
    const id = setInterval(() => setT(Date.now()), refreshMs + jitter);
    return () => clearInterval(id);
  }, [inView, offline, refreshMs]);

  const src = inView && !offline
    ? `${BACKEND_URL}/api/cameras/${cam.console_id}/${cam.camera_id}/snapshot?t=${Math.floor(t)}`
    : null;

  return (
    <div ref={ref} data-testid={`camera-tile-${cam.camera_id}`}
      className="group relative rounded-lg overflow-hidden border border-brand-border bg-brand-ink/90 aspect-video">
      {offline ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-brand-tertiary">
          <VideoOff className="h-6 w-6" />
          <span className="text-[11px] uppercase tracking-[0.12em]">Offline</span>
        </div>
      ) : errored ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-white/50">
          <RefreshCw className="h-5 w-5" />
          <span className="text-[11px]">No snapshot</span>
        </div>
      ) : src ? (
        <img src={src} alt={cam.name} loading="lazy"
          onError={() => setErrored(true)} onLoad={() => setErrored(false)}
          className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-white/30">
          <Video className="h-6 w-6" />
        </div>
      )}

      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent px-2.5 py-2 flex items-center justify-between gap-2">
        <span className="text-[11px] text-white truncate font-medium drop-shadow">{cam.name}</span>
        <a href={cam.live_url} target="_blank" rel="noreferrer" title="Open live view in UniFi Protect"
          data-testid={`camera-live-${cam.camera_id}`}
          className="shrink-0 text-white/80 hover:text-white transition-colors" onClick={(e) => e.stopPropagation()}>
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
      {!offline && (
        <span className="absolute top-2 left-2 h-2 w-2 rounded-full bg-emerald-400 shadow ring-2 ring-black/30"
          title="Connected" />
      )}
    </div>
  );
}

export default function CameraWall({ location = null, showLocationHeaders = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      const { data } = await api.get("/cameras", { params: location ? { location } : {} });
      setConfigured(data.configured);
      setData(data.locations || []);
    } catch (e) {
      setData([]); setLoadError(e.userMessage);
    } finally {
      setLoading(false);
    }
  }, [location]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="cameras-loading">
        {[...Array(8)].map((_, i) => <div key={i} className="aspect-video rounded-lg bg-brand-surface border border-brand-border animate-pulse" />)}
      </div>
    );
  }

  if (loadError) {
    return <div className="text-sm text-brand-neg" data-testid="cameras-error">Cameras couldn't load: {loadError}</div>;
  }
  if (!configured) {
    return <div className="text-sm text-brand-muted" data-testid="cameras-not-configured">No UniFi API key saved yet — cameras are off.</div>;
  }

  const groups = data || [];
  const totalCams = groups.reduce((n, g) => n + g.cameras.length, 0);
  if (!totalCams) {
    return <div className="text-sm text-brand-muted py-8 text-center" data-testid="cameras-empty">No cameras available{location ? " for this location" : ""}.</div>;
  }

  return (
    <div className="space-y-8" data-testid="camera-wall">
      {groups.map((g) => (
        g.cameras.length ? (
          <section key={g.location} data-testid={`camera-group-${g.location}`}>
            {showLocationHeaders && (
              <div className="flex items-baseline justify-between mb-3">
                <h3 className="font-serif text-xl text-brand-ink">{g.location}</h3>
                <span className="text-xs text-brand-tertiary">{g.cameras.length} camera{g.cameras.length === 1 ? "" : "s"}</span>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
              {g.cameras.map((c) => <CameraTile key={`${c.console_id}-${c.camera_id}`} cam={c} />)}
            </div>
          </section>
        ) : null
      ))}
    </div>
  );
}
